import { AlpineComponent } from 'alpinejs';
import Player from '@vimeo/player';

/**
 * Course topic data returned from the backend.
 * See CoursePageController#getCourseTopicDataJSON().
 */
interface CourseTopicData {
	CompletedLink: string;
	Topic: {
		TopicContents: CourseTopicContentData[];
	};
}

interface CourseTopicContentDataBase {
	ID: number;
	Title: string;
	Link: string;
	CallbackUrl: string;
	CanAccess: boolean;
	IsViewed: boolean;
	IsCompleted: boolean;
}

interface CourseTopicVideoData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: true;
	IsLearningMaterial: false;
	IsTryContent: false;
	IsScormChapterContent: false;
	VideoLink: string;
}

interface CourseTopicQuizData extends CourseTopicContentDataBase {
	IsQuiz: true;
	IsVideo: false;
	IsLearningMaterial: false;
	IsTryContent: false;
	IsScormChapterContent: false;
}

interface CourseTopicLearningMaterialData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: false;
	IsLearningMaterial: true;
	IsTryContent: false;
	IsScormChapterContent: false;
}

interface CourseTopicTryContentData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: false;
	IsLearningMaterial: false;
	IsTryContent: true;
	IsScormChapterContent: false;
}

interface CourseTopicScormChapterContentData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: false;
	IsLearningMaterial: false;
	IsTryContent: false;
	IsScormChapterContent: true;
}

type CourseTopicContentData =
	| CourseTopicQuizData
	| CourseTopicVideoData
	| CourseTopicLearningMaterialData
	| CourseTopicTryContentData
	| CourseTopicScormChapterContentData;

export type CourseTopicContentViewedResponse = {
	IsNowCompleted: boolean;
};

type CourseTopicComponentState = {
	course: CourseTopicData | undefined;
	video: CourseTopicVideoData | undefined;
	player: Player | undefined;
	courseCompleted: boolean;
	videoCompletedForFirstTime: boolean;
	init: () => void;
	setVideo: (id: number) => void;
	initVideo: () => Promise<void>;
	hasPreviousContent: () => boolean;
	hasNextContent: () => boolean;
	previousContent: () => void;
	nextContent: () => void;
	goToContent: (content: CourseTopicContentData) => void;
	switchVideo: (video: CourseTopicVideoData) => void;
	markVideoViewed: () => Promise<void>;
	closeVideo: () => void;
	cleanupVideo: () => void;
};

const courseTopic = (
	initialData: CourseTopicData | undefined = undefined,
): AlpineComponent<CourseTopicComponentState> => {
	return {
		course: initialData,
		video: undefined as CourseTopicVideoData | undefined,
		player: undefined as Player | undefined,
		courseCompleted: false,
		videoCompletedForFirstTime: false,

		// opens a video's modal on load when the URL has a `?video={id}` query
		// parameter - used when navigating to a video via another content item's
		// Back/Next buttons, since videos don't have a standalone page of their own
		init() {
			const params = new URLSearchParams(window.location.search);
			const videoId = Number(params.get('video'));
			if (!videoId) return;

			// strip the param immediately so a later reload (e.g. after marking the
			// video viewed for the first time) doesn't reopen the same video again
			params.delete('video');
			const query = params.toString();
			window.history.replaceState(
				null,
				'',
				window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
			);

			this.setVideo(videoId);
			if (this.video) {
				this.$nextTick(() => this.initVideo());
			}
		},

		setVideo(id: number) {
			const video = this.course?.Topic?.TopicContents.find((obj) => obj.ID === id);
			if (!video?.IsVideo) return;

			this.video = video;
			this.videoCompletedForFirstTime = false;
		},

		async initVideo() {
			if (!this.video) return;

			const dialog = document.getElementById('video-dialog') as HTMLDialogElement;
			if (!dialog) return;
			dialog.showModal();

			// create a close event listener so we can clean up when the modal closes
			const closeListener = () => {
				this.cleanupVideo();
				dialog.removeEventListener('close', closeListener);
			};

			dialog.addEventListener('close', closeListener);

			this.markVideoViewed();

			// get vimeo url
			const url = document.getElementById('video-embed')?.dataset.vimeoUrl;
			const player = new Player('video-embed', { url });
			await player.ready();

			this.player = player;
		},

		hasPreviousContent(): boolean {
			const contents = this.course?.Topic?.TopicContents ?? [];
			const index = contents.findIndex((content) => content.ID === this.video?.ID);
			return index > 0;
		},

		hasNextContent(): boolean {
			const contents = this.course?.Topic?.TopicContents ?? [];
			const index = contents.findIndex((content) => content.ID === this.video?.ID);
			return index !== -1 && index < contents.length - 1;
		},

		previousContent() {
			const contents = this.course?.Topic?.TopicContents ?? [];
			const index = contents.findIndex((content) => content.ID === this.video?.ID);
			if (index <= 0) return;

			this.goToContent(contents[index - 1]);
		},

		nextContent() {
			const contents = this.course?.Topic?.TopicContents ?? [];
			const index = contents.findIndex((content) => content.ID === this.video?.ID);
			if (index === -1 || index >= contents.length - 1) return;

			this.goToContent(contents[index + 1]);
		},

		// navigates to the previous/next item in the chapter - if it's another
		// video, switch to it in place, otherwise it has its own page to visit
		goToContent(content: CourseTopicContentData) {
			if (content.IsVideo) {
				this.switchVideo(content);
			} else {
				window.location.href = content.Link;
			}
		},

		switchVideo(video: CourseTopicVideoData) {
			this.video = video;
			this.videoCompletedForFirstTime = false;
			this.player?.loadVideo(video.VideoLink);
			this.markVideoViewed();
		},

		async markVideoViewed() {
			if (!this.video || this.video.IsViewed) return;

			this.videoCompletedForFirstTime = true;

			// fire event back to the server
			const rsp = await fetch(this.video.CallbackUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					contentId: this.video.ID,
				}),
			});

			const data = (await rsp.json()) as CourseTopicContentViewedResponse;

			this.video.IsViewed = true;

			if (data.IsNowCompleted) {
				// the user has now completed the course, so redirect to
				// the complete page when they close the video modal
				this.courseCompleted = true;
			}
		},

		closeVideo() {
			const dialog = document.getElementById('video-dialog') as HTMLDialogElement;
			if (!dialog) return;

			dialog.close();
		},

		// cleanupVideo() is called after the video dialog is closed, either by
		// clicking the close button, or pressing ESC
		cleanupVideo() {
			if (this.courseCompleted && this.course) {
				// this was the last content item in the course, so when the user
				// closes the video, redirect them to the 'completed' page
				window.location.href = this.course.CompletedLink;
			} else {
				this.video = undefined;

				if (this.videoCompletedForFirstTime) {
					// User has completed the video for the first time.
					// Reload the page so the topic progression UI is updated.
					window.location.reload();
				}
			}
		},
	};
};

export default courseTopic;
